import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Users, Target, Award, Handshake, Shield, Recycle, ChevronLeft } from 'lucide-react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import GradeLegend from '@/components/GradeLegend';
import { SaleTypesExplanation } from '@/components/SaleTypesExplanation';
import FAQSection from '@/components/FAQSection';
const About = () => {
  const benefits = [{
    icon: Shield,
    title: 'חסכון כספי משמעותי',
    description: 'אנו מציעים הנחות גדולות בזכות ההסכמים עם הספקים.'
  }, {
    icon: Award,
    title: 'אחריות ואיכות',
    description: 'אתם מקבלים חומרים חדשים שנבדקו.'
  }, {
    icon: Recycle,
    title: 'תרומה לסביבה',
    description: 'שימוש בעודפים מונע ייצור מיותר והטמנת פסולת.'
  }, {
    icon: Handshake,
    title: 'שירות אישי',
    description: 'צוות התמיכה שלנו מלווה אתכם משלב הבחירה ועד האספקה.'
  }];
  const team = [{
    name: 'משה כהן',
    role: 'מנהל כללי ומייסד',
    description: '15 שנות ניסיון בתעשיית הבנייה והחומרים'
  }, {
    name: 'רחל לוי',
    role: 'מנהלת פיתוח עסקי',
    description: 'מתמחה בקשרי ספקים ופיתוח שותפויות אסטרטגיות'
  }, {
    name: 'דוד אברהם',
    role: 'מנהל טכנולוגיה',
    description: 'אחראי על המערכות הטכנולוגיות והחדשנות הדיגיטלית'
  }];
  const navigate = useNavigate();
  return <div className="min-h-screen bg-background">
    <Navigation />

    <main className="pt-4 lg:pt-8 pb-12">
      <div className="container mx-auto px-4">
        {/* Breadcrumbs */}
        <div className="mb-6 mt-0" dir="rtl">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">
                  בית
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator>
                <ChevronLeft className="h-4 w-4" />
              </BreadcrumbSeparator>
              <BreadcrumbItem>
                <BreadcrumbPage>אודות</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            מי אנחנו?
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            ברוכים הבאים ל-Excess-Market. המקום שבו עודפי ייצור ומלאים תקועים הופכים להזדמנויות עיצוב משתלמות. אנחנו כאן כדי לעשות סדר בענף הבנייה והלייף סטייל, לעזור לעסקים לשחרר מלאים איטיים, ולקדם תהליך ירוק ובר-קיימא.
          </p>
        </div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <Card>
            <CardContent className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-lime/10 rounded-lg flex items-center justify-center">
                  <Target className="h-6 w-6 text-lime" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">איך זה עובד?</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                הכוח שלנו הוא בשיתוף פעולה. אנחנו משלימים קונטיינרים עבור יבואנים כדי להוזיל עלויות שילוח, ומפעילים קבוצות רכישה שקופות - ברגע שמגיעים ליעד הכמות, המכירה יוצאת לדרך. כך אנו מביאים אליכם מוצרים חדשים במחירים חסרי תקדים. חיסכון ליבואן - ורווח נקי לכם.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-lime/10 rounded-lg flex items-center justify-center">
                  <Award className="h-6 w-6 text-lime" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">החזון שלנו</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                המוטו שלנו: קונים חכם, בונים ירוק, מעצבים בסטייל. אנו שואפים להפוך מלאי "תקוע" להזדמנות, ולקדם קיימות (Sustainability) כערך מוביל ולא רק כחיסכון. אנו מונעים בזבוז אנרגיה ומשאבים, ומאפשרים לכם ליהנות מעיצוב ברמה גבוהה תוך שמירה על הסביבה.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Benefits */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-foreground text-center mb-12">מה יוצא לכם מזה?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return <Card key={index} className="text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-lime/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="h-8 w-8 text-lime" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-4">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </CardContent>
              </Card>;
            })}
          </div>
        </div>



        {/* Sale Types Explanation */}
        <div className="mb-16 -mx-4">
          <SaleTypesExplanation />
        </div>

        {/* FAQ Section */}
        <div className="mb-16 -mx-4">
          <FAQSection showAboutLink={false} />
        </div>

        {/* CTA */}
        <div className="text-center">
          <Card>
            <CardContent className="p-12">
              <h2 className="text-3xl font-bold text-foreground mb-6">התחילו לחסוך כבר היום</h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                הצטרפו אלינו למהפכה בתעשיית חומרי הבנייה וחסכו כסף תוך תרומה לסביבה
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button onClick={() => navigate('/products')} className="bg-lime text-lime-foreground px-8 py-3 rounded-lg font-medium hover:bg-lime/90 transition-colors">
                  צפו במוצרים
                </button>
                {/* Contact button removed */}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Grade Legend */}
        <div className="mt-16">
          <GradeLegend />
        </div>
      </div>
    </main>

    <Footer />
  </div>;
};
export default About;